import React, {
  useState,
  useEffect,
  useImperativeHandle,
  useCallback,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, ModalBody } from "reactstrap";
import { CHECK_ERROR } from "../../../../../components/alert/Alert";
import { AnnotationPDF } from "../../../../../components/other";
// import { LoadingSpinner } from "../../../../../components/spinners";
import { TreeView, TreeItem } from "../../../../../components/tree";
import { Main } from "../../../../../service/GateWay";
import { _Enums_TypeUpload } from "../../../../action/defaultEnums";
import { actionReloadFile } from "../../Redux/ActionsMeeting";
import TeamPending from "./Pending";

// MUI Icons for file types
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import GridOnIcon from "@mui/icons-material/GridOn";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import ArticleIcon from "@mui/icons-material/Article";
import ImageIcon from "@mui/icons-material/Image";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

const settingData = (data) => {
  const arrData = [];

  // Tạo map để tìm parent nhanh hơn
  const idMap = new Map();
  data.forEach((item) => {
    if (item.id) {
      idMap.set(item.id, item);
    }
  });

  // Tìm các root nodes (items không có parentId hoặc parentId không tồn tại trong data)
  data.forEach((curr) => {
    // Item là root nếu:
    // 1. Không có parentId
    // 2. parentId là null/undefined
    // 3. parentId không tồn tại trong data (orphan node)
    const isRoot = !curr.parentId || !idMap.has(curr.parentId);

    if (isRoot) {
      const element = { ...curr };
      const children = getNestedChildren(data, curr.id);
      if (children.length > 0) {
        element.children = children;
      }
      arrData.push(element);
    }
  });

  return arrData;
};

/**
 * Filter out empty folders (folders with no children and no file path)
 * Keep folders that have children or are actual files
 */
const filterEmptyFolders = (nodes) => {
  return nodes
    .map((node) => {
      if (node.children && node.children.length > 0) {
        // Recursively filter children
        const filteredChildren = filterEmptyFolders(node.children);
        if (filteredChildren.length > 0) {
          return { ...node, children: filteredChildren };
        }
        // If all children are filtered out, this folder is empty
        return null;
      }
      // Keep nodes that have a path (actual files)
      if (node.path) {
        return node;
      }
      // Remove empty folders (no children and no path)
      return null;
    })
    .filter(Boolean);
};

function getNestedChildren(arr, idParent) {
  var children = [];
  arr.forEach((curr, i) => {
    if (curr.parentId === idParent) {
      var grandChildren = getNestedChildren(arr, curr.id);
      if (grandChildren.length) {
        curr.children = grandChildren;
      }
      children.push(curr);
    }
  });
  return children;
}

/**
 * @author Hồ Văn Tuấn
 * @description Hiển thị cây
 */
const TreeFile = React.forwardRef(
  (
    {
      manipulation,
      viewDeleted,
      reloadTable,
      id,
      phienHopId,
      notPopup,
      onClickPDF,
    },
    ref,
  ) => {
    // states
    const [loading, setLoading] = useState(true);
    const [files, setFiles] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [selected, setSelected] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const { isReloadFile } = useSelector((state) => state.meetingRProcess);
    const dispatch = useDispatch();

    // Sử dụng useRef để lưu manipulation và tránh re-create queryData
    const manipulationRef = useRef(manipulation);
    useEffect(() => {
      manipulationRef.current = manipulation;
    }, [manipulation]);

    const queryData = useCallback(
      (id) => {
        setLoading(true);
        manipulationRef.current
          .view(id, true)
          .then((value) => {
            // Kiểm tra nếu value là mảng hợp lệ
            if (!Array.isArray(value)) {
              setFiles([]);
              return;
            }

            // Nếu mảng rỗng, set files rỗng
            if (value.length === 0) {
              setFiles([]);
              return;
            }

            // Xử lý data để tạo cây
            const rawFiles = settingData(value);

            // Debug: Log để kiểm tra (chỉ trong development)
            if (
              process.env.NODE_ENV === "development" &&
              rawFiles.length === 0 &&
              value.length > 0
            ) {
              // eslint-disable-next-line no-console
              console.warn(
                "Không có root node nào được tạo. Có thể tất cả items đều có parentId:",
                value,
              );
            }

            // Filter các folder rỗng (không có file thật bên trong)
            const files = filterEmptyFolders(rawFiles);

            // Hiển thị tất cả file (cả folder và file thường)
            setFiles(files);
          })
          .catch((err) => {
            CHECK_ERROR("Không thể load dữ liệu !");
            if (process.env.NODE_ENV === "development") {
              // eslint-disable-next-line no-console
              console.error("Lỗi khi load file tree:", err);
            }
            setFiles([]);
          })
          .finally(() => {
            dispatch(actionReloadFile(null));
            setLoading(false);
          });
      },
      [dispatch],
    );
    // Call API
    useEffect(() => {
      if (id) queryData(id);
    }, [id, queryData]);

    // Call API by Ref
    useImperativeHandle(ref, () => ({ queryData }));

    const handleToggle = (event, nodeIds) => {
      event.persist();
      let iconClicked = event.target.closest(".MuiTreeItem-iconContainer");
      if (iconClicked) {
        setExpanded(nodeIds);
      }
    };
    const onSelect = useCallback(
      (info) => {
        onClickPDF && onClickPDF(info);
        setSelected(info);
      },
      [onClickPDF],
    );
    const handleReload = useCallback(
      (id) => {
        queryData(id);
      },
      [queryData],
    );

    useEffect(() => {
      isReloadFile && queryData(isReloadFile);
    }, [isReloadFile, queryData]);

    return (
      <div className="p-2">
        <TeamPending loading={loading}>
          {files && files.length !== 0 && (
            <TreeView
              nameId="id"
              expandAll={true}
              expanded={expanded}
              data={files}
              onNodeToggle={handleToggle}
            >
              {viewNodes(
                "root",
                files,
                viewDeleted,
                onSelect,
                manipulation,
                reloadTable,
              )}
            </TreeView>
          )}
        </TeamPending>
        {!notPopup && (
          <Modal
            size="xl"
            isOpen={selected ? true : false}
            toggle={() => setSelected(null)}
            zIndex="1500"
          >
            <ModalBody style={{ height: "35rem", overflowY: "hidden" }}>
              {selected && (
                <AnnotationPDF
                  onReloadFile={handleReload}
                  type={_Enums_TypeUpload.CaNhan}
                  phienHopId={phienHopId}
                  url={Main.getDocUrlByPath(selected.path)}
                  style={{ height: "30.5rem" }}
                />
              )}
            </ModalBody>
          </Modal>
        )}
      </div>
    );
  },
);

const viewNodes = (
  keyParent,
  nodes,
  viewDeleted,
  onSelect,
  manipulation,
  reloadTable,
) => {
  const getIcon = (node) => {
    if (node.children)
      return { IconComponent: FolderOpenIcon, classIcon: "text-blue" };
    const path = (node.path || node.label || "").toLowerCase();
    const ext = path.includes(".") ? path.split(".").pop() : "";
    switch (ext) {
      case "pdf":
        return { IconComponent: PictureAsPdfIcon, classIcon: "text-red" };
      case "doc":
      case "docx":
        return { IconComponent: DescriptionIcon, classIcon: "text-blue" };
      case "xls":
      case "xlsx":
        return { IconComponent: GridOnIcon, classIcon: "text-green" };
      case "ppt":
      case "pptx":
        return {
          IconComponent: SlideshowIcon,
          classIcon: "text-orange",
        };
      case "txt":
        return { IconComponent: ArticleIcon, classIcon: "text-muted" };
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "bmp":
        return { IconComponent: ImageIcon, classIcon: "text-purple" };
      case "zip":
      case "rar":
      case "7z":
        return { IconComponent: FolderZipIcon, classIcon: "text-warning" };
      default:
        return { IconComponent: InsertDriveFileIcon, classIcon: "text-muted" };
    }
  };
  return (
    nodes &&
    nodes.map((node, i) => {
      // Create unique nodeId by combining parent path with index and node.id
      const uniqueNodeId = `${keyParent}-${i}-${node.id}`;
      const { IconComponent, classIcon } = getIcon(node);
      return (
        <TreeItem
          IconComponent={IconComponent}
          classIcon={classIcon}
          key={uniqueNodeId}
          nodeId={uniqueNodeId}
          title={node.label}
          data={{
            Parent: keyParent,
            NodeId: node.id,
            title: node.label,
            label: node.label,
            path: node.path,
          }}
          onSelection={(e, infos) => infos.Parent !== "null" && onSelect(infos)}
        >
          {node.children &&
            viewNodes(
              uniqueNodeId,
              node.children,
              viewDeleted,
              onSelect,
              manipulation,
              reloadTable,
            )}
        </TreeItem>
      );
    })
  );
};

export default TreeFile;
